import { QueryClient, useQuery } from '@tanstack/react-query'
import { OrgDetailsDocument } from '@vendia/control-plane-api'
import { useContext } from 'react'
import { useRecoilValue } from 'recoil'

import { authContext } from '../auth/auth-context'
import { selectedRoleState } from '../state'
import useControlPlaneApi from './use-control-plane-api'

const QUERY_KEY = 'getOrgDetails'
const FIVE_MINUTES = 300000

export type GetOrgDetails = NonNullable<ReturnType<typeof useGetOrgDetails>['data']>['getOrganization']

export const useGetOrgDetails = ({ ...options } = {}) => {
  const { auth } = useContext(authContext)
  const selectedRoleName = useRecoilValue(selectedRoleState).name

  const client = useControlPlaneApi()
  return useQuery({
    queryKey: [QUERY_KEY, selectedRoleName],
    queryFn: async () => {
      const data = await client.graphqlFetch({ query: OrgDetailsDocument })
      // Only throw if there's no data at all - partial errors (e.g. some users
      // failing to fetch) should not prevent the UI from functioning
      if (data.errors && !data.getOrganization) {
        throw new Error(JSON.stringify(data.errors))
      }
      return data
    },
    staleTime: FIVE_MINUTES,
    enabled: auth.authenticated && !!selectedRoleName,
    ...options,
  })
}

export const refetchOrgDetails = ({ queryClient }: { queryClient: QueryClient }) =>
  queryClient.invalidateQueries({ queryKey: [QUERY_KEY], refetchType: 'all' })
